// MediaViewer.tsx — fullscreen photo viewer.
//
// Opens when a media frame is tapped (the frame is a <button>, so PostCard's card-level
// click-through to the comments is not triggered — tapping the photo inspects the photo,
// tapping anywhere else on the card opens the post).
//
// FIT shows the whole image; tapping again switches to ACTUAL, which renders the image at
// its uploaded pixel resolution and lets you pan around it.

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import type { PostImage } from '@/core/models/post';
import { Icon } from '@/icons';
import {
  INITIAL,
  canZoom,
  panBy,
  stepIndex,
  toggleZoom,
  type PanState,
  type Size,
} from './zoomPan';

/* ── Styled ──────────────────────────────────────────────────────────────────── */

const Scrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 300; /* above Sheet (201) and Dialog (100) */
  width: 100dvw;
  height: 100dvh;
  background: rgba(4, 5, 12, 0.94);
  /* No backdrop-filter: it is the most expensive compositing op on iOS and pointless
     behind a near-opaque scrim. */
  overscroll-behavior: contain;
  display: grid;
  grid-template-rows: 48px 1fr auto;
  padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom)
    env(safe-area-inset-left);
`;

const Rail = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 12px;
  min-height: 48px;
`;

const Counter = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: #b9c4cc;
`;

const Spacer = styled.span`
  flex: 1;
`;

const RailBtn = styled.button`
  appearance: none;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: rgba(255, 255, 255, 0.06);
  color: #e8eef2;
  display: inline-grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.13);
  }
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const Stage = styled.div<{ $mode: PanState['mode']; $zoomable: boolean }>`
  position: relative;
  overflow: hidden;
  display: grid;
  place-items: center;
  /* MANDATORY. A grid item defaults to min-height:auto, so a tall image would expand this
     1fr row (and the whole viewer) past the viewport instead of being scaled down to fit. */
  min-height: 0;
  min-width: 0;
  touch-action: none; /* the viewer owns its gestures */
  cursor: ${({ $mode, $zoomable }) =>
    $mode === 'actual' ? 'grab' : $zoomable ? 'zoom-in' : 'default'};
  &:active {
    cursor: ${({ $mode }) => ($mode === 'actual' ? 'grabbing' : 'inherit')};
  }
`;

const Img = styled.img<{ $mode: PanState['mode']; $motion: boolean }>`
  display: block;
  border-radius: ${({ theme }) => theme.radius.sm};
  user-select: none;
  -webkit-user-drag: none;
  /* FIT caps are applied inline in real pixels from the measured stage: a percentage
     max-height does not reliably resolve against a grid area, so the image would keep its
     full intrinsic height and push the viewer past the viewport. */
  ${({ $mode }) =>
    $mode === 'fit'
      ? `width: auto; height: auto; object-fit: contain;`
      : /* ACTUAL — the uploaded resolution, 1:1. */
        `max-width: none; max-height: none;`}
  transform: translate3d(var(--tx, 0px), var(--ty, 0px), 0);
  transition: ${({ $motion }) => ($motion ? 'transform 180ms cubic-bezier(.16,1,.3,1)' : 'none')};
  &[data-dragging='true'] {
    transition: none;
  }
`;

const Caption = styled.p`
  margin: 0;
  padding: 10px 16px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #b9c4cc;
  max-width: 900px;
  justify-self: center;
`;

const Segs = styled.div`
  display: flex;
  gap: 6px;
  justify-content: center;
  padding-top: 6px;
`;

const Seg = styled.button<{ $on: boolean }>`
  appearance: none;
  border: 0;
  background: transparent;
  display: block;
  padding: 11px 2px; /* 24px hit area around a 2px bar */
  cursor: pointer;
  &::after {
    content: '';
    display: block;
    width: 22px;
    height: 2px;
    background: ${({ theme, $on }) => ($on ? theme.color.accentCyan : 'rgba(232,238,242,0.28)')};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

/** Horizontal drag (px) past which the viewer moves to the next photo. */
const SWIPE_THRESHOLD = 56;

/* ── Component ───────────────────────────────────────────────────────────────── */

export interface MediaViewerProps {
  items: PostImage[];
  index: number;
  onIndexChange: (i: number) => void;
  onClose: () => void;
  reduceMotion?: boolean;
}

export function MediaViewer({
  items,
  index,
  onIndexChange,
  onClose,
  reduceMotion = false,
}: MediaViewerProps) {
  const [pan, setPan] = useState<PanState>(INITIAL);
  const [natural, setNatural] = useState<Size>({ width: 0, height: 0 });
  const [dragging, setDragging] = useState(false);
  // Stage size lives in state, not read off the ref during render: refs do not trigger a
  // re-render, so a resized window would leave `zoomable` and the pan clamps stale.
  const [stage, setStage] = useState<Size>({ width: 0, height: 0 });

  const stageRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const dragOrigin = useRef<{ x: number; y: number } | null>(null);
  const swipeStart = useRef<{ x: number; y: number } | null>(null);

  const item = items[index];
  const total = items.length;

  // Keep the stage measurement current (rotation, window resize, iOS URL-bar collapse).
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setStage({ width: r.width, height: r.height });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const go = useCallback(
    (delta: number) => {
      setPan(INITIAL); // every photo opens fit
      onIndexChange(stepIndex(index, delta, total));
    },
    [index, total, onIndexChange],
  );

  // Focus management: remember what was focused, move focus in, restore on close.
  useEffect(() => {
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    surfaceRef.current?.focus();
    return () => restoreFocusRef.current?.focus?.();
  }, []);

  // Lock background scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Keyboard map + a simple focus trap.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowRight' && total > 1) {
        e.preventDefault();
        go(1);
        return;
      }
      if (e.key === 'ArrowLeft' && total > 1) {
        e.preventDefault();
        go(-1);
        return;
      }
      if (e.key === 'z' || e.key === 'Z') {
        e.preventDefault();
        setPan((p) => toggleZoom(p, natural, stage));
        return;
      }
      if (e.key === 'Tab') {
        // Trap: cycle focus within the viewer.
        const focusables = surfaceRef.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), [href], [tabindex]:not([tabindex="-1"])',
        );
        if (!focusables || focusables.length === 0) return;
        const list = Array.from(focusables);
        const first = list[0];
        const last = list[list.length - 1];
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [go, onClose, total, natural, stage]);

  if (!item) return null;

  const zoomable = canZoom(natural, stage);

  function onPointerDown(e: React.PointerEvent) {
    dragOrigin.current = { x: e.clientX, y: e.clientY };
    swipeStart.current = { x: e.clientX, y: e.clientY };
    if (pan.mode === 'actual') {
      setDragging(true);
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    }
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!dragging || !dragOrigin.current) return;
    const dx = e.clientX - dragOrigin.current.x;
    const dy = e.clientY - dragOrigin.current.y;
    dragOrigin.current = { x: e.clientX, y: e.clientY };
    setPan((p) => panBy(p, dx, dy, natural, stage));
  }

  function endDrag(e?: React.PointerEvent) {
    // While FIT, a horizontal drag moves through the deck; while ACTUAL the same gesture
    // is panning the photo, so swiping is deliberately disabled there.
    if (pan.mode === 'fit' && total > 1 && swipeStart.current && e) {
      const dx = e.clientX - swipeStart.current.x;
      const dy = e.clientY - swipeStart.current.y;
      if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        go(dx < 0 ? 1 : -1);
      }
    }
    swipeStart.current = null;
    setDragging(false);
    dragOrigin.current = null;
  }

  function onStageClick(e: React.MouseEvent) {
    // Clicking the backdrop (not the photo) closes.
    if (e.target === stageRef.current) {
      onClose();
      return;
    }
    const rect = stageRef.current?.getBoundingClientRect();
    const origin = rect
      ? { x: e.clientX - (rect.left + rect.width / 2), y: e.clientY - (rect.top + rect.height / 2) }
      : { x: 0, y: 0 };
    setPan((p) => toggleZoom(p, natural, stage, origin));
  }

  const body = (
    <Scrim
      role="dialog"
      aria-modal="true"
      aria-label={`Image viewer, image ${index + 1} of ${total}`}
      ref={surfaceRef}
      tabIndex={-1}
      data-testid="media-viewer"
    >
      <Rail>
        {total > 1 && (
          <Counter aria-label={`Image ${index + 1} of ${total}`}>
            {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
          </Counter>
        )}
        <Spacer />
        {total > 1 && (
          <>
            <RailBtn type="button" onClick={() => go(-1)} aria-label="Previous image">
              <Icon name="chevron-left" size={18} />
            </RailBtn>
            <RailBtn type="button" onClick={() => go(1)} aria-label="Next image">
              <Icon name="chevron-right" size={18} />
            </RailBtn>
          </>
        )}
        <RailBtn
          type="button"
          onClick={() => setPan((p) => toggleZoom(p, natural, stage))}
          disabled={!zoomable}
          aria-label={
            pan.mode === 'actual' ? 'Fit image to screen' : 'View at full uploaded resolution'
          }
          aria-pressed={pan.mode === 'actual'}
        >
          <Icon name={pan.mode === 'actual' ? 'zoom-out' : 'zoom-in'} size={18} />
        </RailBtn>
        <RailBtn type="button" onClick={onClose} aria-label="Close image viewer">
          <Icon name="close" size={18} />
        </RailBtn>
      </Rail>

      <Stage
        ref={stageRef}
        $mode={pan.mode}
        $zoomable={zoomable}
        onClick={onStageClick}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <Img
          key={item.url}
          src={item.url}
          alt={item.alt || ''}
          $mode={pan.mode}
          $motion={!reduceMotion}
          data-dragging={dragging ? 'true' : 'false'}
          data-mode={pan.mode}
          data-testid="media-viewer-image"
          style={
            {
              '--tx': `${pan.x}px`,
              '--ty': `${pan.y}px`,
              ...(pan.mode === 'actual' && natural.width
                ? // The uploaded resolution, 1:1.
                  { width: `${natural.width}px`, height: `${natural.height}px` }
                : stage.height
                  ? { maxWidth: `${stage.width}px`, maxHeight: `${stage.height}px` }
                  : {}),
            } as unknown as React.CSSProperties
          }
          onLoad={(e) => {
            const el = e.currentTarget;
            setNatural({ width: el.naturalWidth, height: el.naturalHeight });
          }}
        />
      </Stage>

      <div>
        {total > 1 && (
          <Segs role="tablist" aria-label="Images">
            {items.map((it, i) => (
              <Seg
                key={`${it.url}-${i}`}
                type="button"
                $on={i === index}
                role="tab"
                aria-selected={i === index}
                aria-label={`Image ${i + 1}`}
                onClick={() => {
                  setPan(INITIAL);
                  onIndexChange(i);
                }}
              />
            ))}
          </Segs>
        )}
        {item.alt && <Caption>{item.alt}</Caption>}
      </div>
    </Scrim>
  );

  return createPortal(body, document.body);
}
